/**
 * @module Actions.AcademySetup
 *
 */
import { Dispatch } from "react";
import { ActionType, action } from "typesafe-actions";
import { RequestError } from "typedefs";
import { store } from "app/Store";
import { fetchGet, fetchPut } from "app/utils/request";
import {
  IAcademySetupApiResponse,
  completionFromApi,
  optionalItemApiIds,
  optionalStatesFromApi,
} from "app/components/AcademySetup/completion";
import {
  AcademySetupCompletion,
  AcademySetupCtaSource,
  AcademySetupItemKey,
  AcademySetupOptionalState,
  AcademySetupOptionalStates,
  withCompletion,
} from "app/components/AcademySetup/items";
import {
  completionTransitions,
  newlyCompleteKeys,
  settledOptionalStates,
} from "app/components/AcademySetup/transitions";
import {
  CompletionSources,
  recordCompletionSources,
} from "app/components/AcademySetup/completionSources";
import { AcademySetupState } from "app/reducers/academySetup";
import {
  trackChecklistItemCompleted,
  trackPhaseCompleted,
} from "app/utils/analytics-helpers/academySetup";

const pending = (ownerId: string) =>
  action("@academySetup.fetch.pending", ownerId);
const error = (err: RequestError) =>
  action("@academySetup.fetch.error", err);
const success = (
  ownerId: string,
  data: AcademySetupCompletion,
  optionalStates: AcademySetupOptionalStates,
) =>
  action("@academySetup.fetch.success", { ownerId, data, optionalStates });
const optionalMarked = (
  key: AcademySetupItemKey,
  state: AcademySetupOptionalState,
) => action("@academySetup.optional.marked", { key, state });

/** Remembers the CTA that launched `key` so its completion can be attributed and timed. */
export const markSetupCta = (
  key: AcademySetupItemKey,
  source: AcademySetupCtaSource,
) => action("@academySetup.cta.clicked", { key, source, at: Date.now() });

/** The PUT behind an optimistic mark succeeded. */
const optionalConfirmed = (key: AcademySetupItemKey) =>
  action("@academySetup.optional.confirmed", key);
/**
 * Undoes an optimistic mark whose PUT failed; `previous` undefined removes it.
 * `dropCta` takes the CTA recorded for the mark with it.
 */
const optionalReverted = (
  key: AcademySetupItemKey,
  previous: AcademySetupOptionalState | undefined,
  dropCta: boolean,
) => action("@academySetup.optional.reverted", { key, previous, dropCta });

const thisActions = {
  pending,
  error,
  success,
  optionalMarked,
  optionalConfirmed,
  optionalReverted,
  markSetupCta,
};

export type AcademySetupAction = ActionType<typeof thisActions>;

/**
 * Fires the item/phase completion events for whatever flipped between `prev`
 * and the state about to be stored, and records which CTA (if any) each row
 * that just became complete is owed to, for setup_checklist_completed's
 * per-source counters. `userId` null (signed out meanwhile) records nothing.
 */
const trackCompletionTransitions = (
  prev: AcademySetupState,
  data: AcademySetupCompletion,
  optionalStates: AcademySetupOptionalStates,
  userId: string | null,
) => {
  const prevItems = prev.data
    ? withCompletion(prev.data, prev.optionalStates)
    : null;
  const nextItems = withCompletion(data, optionalStates);
  const { completedKeys, completedPhases } = completionTransitions(
    prevItems,
    nextItems,
  );

  const sources: CompletionSources = {};
  newlyCompleteKeys(prevItems, nextItems).forEach((key) => {
    const cta = prev.ctas[key];
    sources[key] = cta ? cta.source : "organic_nav";
  });
  if (userId && Object.keys(sources).length) {
    recordCompletionSources(userId, sources);
  }

  completedKeys.forEach((key) => {
    const item = nextItems.find((entry) => entry.key === key);
    if (!item) {
      return;
    }
    const cta = prev.ctas[key] || null;
    trackChecklistItemCompleted(
      key,
      item.phase,
      cta ? cta.source : "organic_nav",
      cta ? Date.now() - cta.at : undefined,
    );
  });
  completedPhases.forEach((phase) => trackPhaseCompleted(phase));
};

// Forced fetches bypass the dedupe, so two reads can be in flight at once.
// Only the latest issued may write: a slower, earlier response landing after
// it would roll the checklist back to pre-save completion (wrong count and
// rail target) and replay the completion events on the next refetch.
let latestRequest = 0;
// Account the in-flight request belongs to: only that account's mounts may
// dedupe onto it (sign-out is an in-app navigation, so a request can outlive
// its coach).
let inFlightUserId: string | null = null;
// Latest optional-mark write issued per item. The PUTs are not versioned
// server-side, so if two writes to one item were ever in flight together
// only the newest may settle the row: an older one confirming or reverting
// afterwards would undo the newer mark and clear its pending flag early.
const latestOptionalWrite: Partial<Record<AcademySetupItemKey, number>> = {};

/**
 * A mark whose PUT confirmed before the first GET landed. With nothing
 * fetched to diff against, its transitions wait for that first response,
 * which otherwise reports no completions at all: the response's own flags
 * count as pre-existing, and only the held marks flip. `previous` is the
 * state before the item's earliest held mark, so visited-then-skipped
 * before the load still reads as one completion.
 */
interface HeldMark {
  userId: string;
  key: AcademySetupItemKey;
  previous: AcademySetupOptionalState | undefined;
}
let heldMarks: HeldMark[] = [];

const holdMark = (mark: HeldMark) => {
  const held = heldMarks.some(
    (entry) => entry.userId === mark.userId && entry.key === mark.key,
  );
  if (!held) {
    heldMarks.push(mark);
  }
};

const takeHeldMarks = (userId: string): HeldMark[] => {
  const mine = heldMarks.filter((mark) => mark.userId === userId);
  heldMarks = heldMarks.filter((mark) => mark.userId !== userId);
  return mine;
};

/**
 * @param force Skips the dedupe guard. The completion alerts read the store
 * the moment this resolves, so being served by someone else's in-flight
 * request would leave them looking at pre-save completion.
 */
export function fetchAcademySetup(force = false) {
  return (dispatch: Dispatch<AcademySetupAction>) => {
    const state = store.getState();
    const userId = state.user.self?.id;
    if (!userId) {
      return Promise.resolve();
    }
    // Concurrent mounts (sidebar badge + feed banner) dedupe to one flight;
    // each fresh mount after that refetches so completion stays current. A
    // flight started for another account never serves this one: its response
    // is dropped below as superseded.
    if (
      state.academySetup.pending &&
      !force &&
      inFlightUserId === userId
    ) {
      return Promise.resolve();
    }

    dispatch(pending(userId));
    latestRequest += 1;
    const requestId = latestRequest;
    inFlightUserId = userId;

    // The consolidated core endpoint aggregates all nine checklist sources
    // server-side (2026-09-03) — one request per fetch.
    return (
      fetchGet("academy/setup", {}, { version: 1 }) as Promise<
        IAcademySetupApiResponse
      >
    )
      .then((response) => {
        if (requestId !== latestRequest) {
          return;
        }
        // Signed out meanwhile: nothing to store or report for that account.
        // The flight is released so the same coach signing back in fetches.
        if (store.getState().user.self?.id !== userId) {
          inFlightUserId = null;
          return;
        }
        const stored = store.getState().academySetup;
        // A slice cached for another account (the store survives sign-out)
        // is neither merged with nor diffed against: the new coach's first
        // response is a first load, not a set of completions.
        const prev: AcademySetupState =
          stored.ownerId === userId
            ? stored
            : {
                ...stored,
                data: null,
                optionalStates: {},
                pendingOptional: {},
                ctas: {},
              };
        const data = completionFromApi(response);
        // Same merge the reducer stores.
        const optionalStates = {
          ...prev.optionalStates,
          ...optionalStatesFromApi(response),
        };
        // An optimistic mark whose PUT is still out counts on neither side:
        // its own transition fires when the PUT confirms, and a phase it
        // would close must not be reported before then.
        const after = settledOptionalStates(optionalStates, prev.pendingOptional);
        trackCompletionTransitions(
          {
            ...prev,
            optionalStates: settledOptionalStates(
              prev.optionalStates,
              prev.pendingOptional,
            ),
          },
          data,
          after,
          userId,
        );
        // A first response reports nothing above (arriving complete is not
        // completing) except the marks confirmed before it landed, diffed
        // here against the response with their own state undone.
        const held = takeHeldMarks(userId);
        if (!prev.data && held.length) {
          const before = { ...after };
          held.forEach(({ key, previous }) => {
            if (previous === undefined) {
              delete before[key];
            } else {
              before[key] = previous;
            }
          });
          trackCompletionTransitions(
            { ...prev, data, optionalStates: before },
            data,
            after,
            userId,
          );
        }
        dispatch(success(userId, data, optionalStates));
      })
      .catch((err) => {
        if (requestId !== latestRequest) {
          return;
        }
        if (store.getState().user.self?.id !== userId) {
          inFlightUserId = null;
          return;
        }
        dispatch(error(err));
      });
  };
}

/**
 * Records that the coach visited or skipped an optional checklist item. The
 * store updates first so the row completes on the click; the BE persists it
 * per coach through PUT academy/setup/items/:item_id { status } (idempotent,
 * live 2026-09-04), addressed by the BE's id for the item, not the checklist
 * key. A failed write rolls the optimistic mark back: the fetch merges the
 * server's states over the local ones, so an unpersisted mark would
 * otherwise survive every later GET for the session.
 */
export function markOptionalItem(
  key: AcademySetupItemKey,
  state: AcademySetupOptionalState,
) {
  return (dispatch: Dispatch<AcademySetupAction>) => {
    const { academySetup: prev, user } = store.getState();
    // The result lands only while the account that made the mark is still
    // signed in; after a sign-out the slice belongs to someone else. The
    // checklist's own CTAs never write twice for one item (a mark completes
    // the row at once and both check `complete`); the payment guard's
    // Offline Only does overwrite a visited Stripe row with skipped, and a
    // mark can land before the checklist has loaded, so the write guard
    // below is a real path, not only insurance.
    const userId = user.self?.id ?? null;
    const stillCurrent = () =>
      (store.getState().user.self?.id ?? null) === userId;
    // The slice may still be another account's (signed out and back in
    // before this account's first fetch reset it); its states say nothing
    // about this account's, so the mark then has no prior state to restore
    // on a failed write or to diff against.
    const previous =
      prev.ownerId === userId ? prev.optionalStates[key] : undefined;
    // Whose slice the optimistic mark goes into. Should this account's first
    // fetch replace that slice while the PUT is out, the mark is gone with
    // it, and the slice then holds this account's own server state.
    const markOwner = prev.ownerId;
    // A visit or skip can complete a phase without any flag changing. The
    // events are durable, so they wait for the server to accept the mark;
    // the row itself completes optimistically. The diff is taken against
    // the store as it stands when the PUT confirms — a flag that landed
    // meanwhile counts — with every other unconfirmed mark left out.
    const track = () => {
      const current = store.getState().academySetup;
      if (!current.data || current.ownerId !== userId) {
        // Nothing of this account's fetched to diff against yet: the payment
        // guard's Offline Only can confirm before the sidebar's first GET
        // lands, or after it failed, or while the slice is still another
        // account's. The first response reports the mark (fetchAcademySetup).
        if (userId) {
          holdMark({ userId, key, previous });
        }
        return;
      }
      const after = settledOptionalStates(
        current.optionalStates,
        current.pendingOptional,
      );
      const before = { ...after };
      if (previous === undefined) {
        delete before[key];
      } else {
        before[key] = previous;
      }
      trackCompletionTransitions(
        { ...current, optionalStates: before },
        current.data,
        after,
        userId,
      );
    };
    dispatch(optionalMarked(key, state));
    const writeId = (latestOptionalWrite[key] || 0) + 1;
    latestOptionalWrite[key] = writeId;
    // Superseded by a newer write to the same item, or by a sign-out.
    const settles = () =>
      latestOptionalWrite[key] === writeId && stillCurrent();
    const itemId = optionalItemApiIds[key];
    if (!itemId) {
      dispatch(optionalConfirmed(key));
      track();
      return Promise.resolve();
    }
    return fetchPut(
      `academy/setup/items/${itemId}`,
      { status: state },
      { version: 1 },
    ).then(
      () => {
        if (settles()) {
          dispatch(optionalConfirmed(key));
          track();
        }
      },
      () => {
        // Only the slice that took the mark is rolled back: one this
        // account's fetch has since replaced no longer holds the mark, and
        // what it holds instead is this account's own state.
        if (settles() && store.getState().academySetup.ownerId === markOwner) {
          // A skip records the checklist as its CTA only so the skip's own
          // completion can be attributed; with the skip undone, that record
          // would attribute (and time) whatever completes the item later. A
          // visit's CTA stays: it opened the page the coach is now on, so a
          // completion there is still its doing.
          dispatch(optionalReverted(key, previous, state === "skipped"));
        }
      },
    );
  };
}

export const actions = {
  fetchAcademySetup,
  markOptionalItem,
  markSetupCta,
};
